import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import Spinner from '@vtex/styleguide/lib/Spinner'
import Button from '@vtex/styleguide/lib/Button'
import Tag from '@vtex/styleguide/lib/Tag'

import { PICKUP_DELIVERY_CHANNEL } from '@/checkout-instore-legacy/shipping/constants'
import * as OMSFetcher from '@/checkout-instore-legacy/fetchers/OMSFetcher'
import {
  getShippingEstimateMessage,
  getScheduledShippingEstimateMessage,
  isDeliverySlaType,
  isPickupSlaType,
} from '@/checkout-instore-legacy/shipping/utils'
import { extractOrderPlacedHeaderInfo } from '@/checkout-instore-legacy/order/utils'
import Price from '@/checkout-instore-legacy/components/Commons/Price'
import { ProductSellerName } from '@/checkout-instore-legacy/components/Commons/ProductSummary/ProductName/ProductSellerName'
import Bullet from '@/checkout-instore-legacy/components/Commons/Bullet'
import { getDefaultSellerId } from '@/checkout-instore-legacy/seller/utils'

function isNotPickupSlaType(selectedSlaType) {
  return isDeliverySlaType(selectedSlaType) || isPickupSlaType(selectedSlaType)
}
class Header extends Component {
  constructor(props) {
    super(props)
    this.state = {
      order: props.order,
    }
    if (
      isNotPickupSlaType(props.pkg.selectedSlaType) &&
      this.state.order &&
      this.state.order.orderId
    ) {
      OMSFetcher.getOrder(this.state.order.orderId).then((newOrder) => {
        const headerExtraInfo = extractOrderPlacedHeaderInfo(newOrder)
        if (headerExtraInfo) {
          this.props.setOrderPlacedInfo(headerExtraInfo)
        }
      })
    }
  }

  checkIsSchedulable(selectedSlaObj) {
    return (
      selectedSlaObj &&
      selectedSlaObj.availableDeliveryWindows &&
      selectedSlaObj.availableDeliveryWindows.length > 0
    )
  }

  getTitle(pkg, numberOfPackages) {
    const { index, pkgHeaderIndex, pkgTotalCount } = pkg

    const idx = pkgHeaderIndex || index + 1
    const totalCount = pkgTotalCount || numberOfPackages

    return (
      <FormattedMessage
        id="packageTitle"
        values={{
          index: idx,
          numberOfPackages: totalCount,
          type: pkg.selectedSlaType.split('-')[0],
        }}
      />
    )
  }

  wrapSubtitleIfInvalidPkg(subtitle, pkg) {
    return pkg.isValid ? (
      subtitle
    ) : (
      <span className="c-warning">{subtitle}</span>
    )
  }

  getSubtitle(pkg) {
    if (!pkg.selectedSlaObj) {
      return null
    }
    const { pkgType } = this.props

    const { selectedSlaObj, seller } = pkg

    const isSchedulable = this.checkIsSchedulable(selectedSlaObj)
    const isScheduled = !!pkg.deliveryWindow
    const isPickup =
      pkg.selectedSlaObj.deliveryChannel === PICKUP_DELIVERY_CHANNEL

    const sellerHeader =
      getDefaultSellerId() !== seller ? (
        <span data-testid="package-header-seller-name" className="mb3-s pl3-ns">
          <ProductSellerName sellerId={seller} type={pkgType} />
        </span>
      ) : null

    if (isPickup) {
      return this.wrapSubtitleIfInvalidPkg(
        <span>
          <span className="flex flex-column-s flex-row-ns">
            {getShippingEstimateMessage(selectedSlaObj.shippingEstimate)}
            <Bullet radius={1} className="bg-c-muted-2 mh3 dib" />
            <FormattedMessage id="shippingSelectedPickupPoint" />{' '}
            {pkg.selectedSlaObj.pickupStoreInfo.friendlyName}
          </span>
          {sellerHeader}
        </span>,
        pkg
      )
    }

    if (!isSchedulable) {
      return this.wrapSubtitleIfInvalidPkg(
        <span className="flex flex-column-s flex-row-ns">
          <span className="inline-flex items-center mb3-s">
            {getShippingEstimateMessage(selectedSlaObj.shippingEstimate)}
            <Bullet radius={1} className="bg-c-muted-2 mh3 dib" />
            <Price value={pkg.price} />
          </span>
          {sellerHeader}
        </span>,
        pkg
      )
    }

    if (!isScheduled) {
      return this.wrapSubtitleIfInvalidPkg(
        <FormattedMessage id="shippingWaitingForScheduling" />,
        pkg
      )
    }

    return this.wrapSubtitleIfInvalidPkg(
      <span className="flex flex-column-s flex-row-ns">
        <span className="inline-flex items-center mb3-s">
          {getScheduledShippingEstimateMessage(selectedSlaObj.deliveryWindow)}
          <Bullet radius={1} className="bg-c-muted-2 mh3 dib" />
          <Price value={pkg.price} />
        </span>
        {sellerHeader}
      </span>,
      pkg
    )
  }

  render() {
    const { pkg, numOfPackages, tag, button, className, loading } = this.props

    const title = this.getTitle(pkg, numOfPackages, this.state.order)
    const subtitle = this.getSubtitle(pkg)

    return (
      <div className={`flex-ns items-center bg-base ${className}`}>
        <div className="flex flex-auto">
          <div>
            <div className="ma0 flex flex-auto items-center f4 mb2 black-80">
              <div>{title}</div>
              {tag && (
                <span className="ml5 f0">
                  <Tag bgColor="#FF4C4C" color="#fff">
                    {tag}
                  </Tag>
                </span>
              )}
              {loading && (
                <div className="pl4 f0">
                  <Spinner size={20} />
                </div>
              )}
            </div>
            {loading ? (
              <span className="pv4 lh-title mocked light-silver text-loading">
                {'Loading shipping method'}
              </span>
            ) : (
              (subtitle || (button && button.inline)) && (
                <div className="flex-ns items-center mid-gray lh-copy f6 mv2 mb4">
                  <span className="mr3">{subtitle}</span>
                  {button && button.inline && (
                    <button
                      className="bn ttu c-action-primary f6 fw5 pointer vtex-button"
                      isLoading={loading}
                      style={{
                        padding: '$space-0',
                        margin: '$space-0',
                        background: 'transparent',
                      }}
                      onClick={(e) => button.onClick(e)}
                    >
                      {button.label}
                    </button>
                  )}
                </div>
              )
            )}
          </div>
        </div>
        {button && !button.inline && (
          <React.Fragment>
            <div className="dn db-ns">
              <Button
                onClick={(e) => button.onClick(e)}
                variation="secondary"
                isLoading={loading}
                size="small"
              >
                {button.label}
              </Button>
            </div>
            <div className="pt4 db dn-ns">
              <Button
                block
                onClick={(e) => button.onClick(e)}
                variation="secondary"
                isLoading={loading}
                size="small"
              >
                {button.label}
              </Button>
            </div>
          </React.Fragment>
        )}
      </div>
    )
  }
}

Header.defaultProps = {
  className: 'ph6 pv5',
}

Header.propTypes = {
  pkg: PropTypes.object.isRequired,
  numOfPackages: PropTypes.number,
  inlineButton: PropTypes.bool,
  button: PropTypes.oneOfType([
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      onClick: PropTypes.func.isRequired,
      inline: PropTypes.bool,
    }),
    PropTypes.bool, // allows sending "false", for checks on whether the button should be rendered or not
  ]),
  className: PropTypes.string,
  loading: PropTypes.bool,
  tag: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.bool, // allows sending "false", for checks on whether the button should be rendered or not
  ]),
  order: PropTypes.object,
  setOrderPlacedInfo: PropTypes.func,
  pkgType: PropTypes.string,
}

export default Header
